<!-- 当前运营控制台源码维护说明，更新时间：2026-09-02 CST。消息已读与 actionPath 均使用控制台真实接口。 -->
<!-- HotX Console 0.1.109 消息游客态。实际修改时间：2026-09-16 13:54:40 CST；精确父基线：cf5594169b784b215a2924abbfc0042baef03cbf。 -->
<template>
  <div class="notification-page">
    <header>
      <div><p>MESSAGE CENTER</p><h1>消息中心</h1></div>
      <el-button :loading="readingAll" :disabled="!unread || readingAll" @click="readAll">全部已读</el-button>
    </header>
    <section v-loading="loading">
      <article
        v-for="item in items"
        :key="item.id"
        :class="{ unread: !item.readAt, actionable: Boolean(internalActionPath(item.actionPath)) }"
        @click="openNotification(item)"
      >
        <i></i>
        <div>
          <div class="title">
            <b>{{ item.title }}</b>
            <time>{{ formatTime(item.createdAt) }}</time>
          </div>
          <p>{{ item.content }}</p>
        </div>
      </article>
      <el-empty v-if="!loading && !items.length" :description="hasConsoleSession() ? '暂无消息' : '当前未登录，登录后可查看账户消息。'" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { usePointStore } from '../stores/points';
import { pointApi, pointErrorMessage, type InboxNotification } from '../api/pointApi';
import { hasConsoleSession } from '../api/client';

const items = ref<InboxNotification[]>([]);
const unread = ref(0);
const loading = ref(false);
const readingAll = ref(false);
const points = usePointStore();
const router = useRouter();
const openingIds = new Set<number>();

function formatTime(value: string) {
  return new Date(value).toLocaleString();
}

async function load() {
  loading.value = true;
  try {
    const data = await pointApi.notifications({ page: 1, size: 100 });
    items.value = data.items || [];
    unread.value = data.unreadCount || 0;
  } finally {
    loading.value = false;
  }
}

async function refresh() {
  await load();
  await points.refreshNotifications().catch(() => undefined);
}

function internalActionPath(actionPath?: string) {
  if (!actionPath) return null;
  try {
    const target = new URL(actionPath, window.location.origin);
    if (target.origin !== window.location.origin || !target.pathname.startsWith('/')) return null;
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return null;
  }
}

async function markRead(item: InboxNotification) {
  if (item.readAt) {
    return;
  }

  await pointApi.markNotificationRead(item.id);
  item.readAt = new Date().toISOString();
  unread.value = Math.max(0, unread.value - 1);
  await points.refreshNotifications().catch(() => undefined);
}

async function openNotification(item: InboxNotification) {
  if (openingIds.has(item.id)) return;
  openingIds.add(item.id);
  try {
    await markRead(item);
  } catch (error) {
    ElMessage.error(pointErrorMessage(error, '消息标记失败，请稍后重试。'));
  } finally {
    openingIds.delete(item.id);
  }

  const actionPath = internalActionPath(item.actionPath);
  if (actionPath) {
    await router.push(actionPath);
  }
}

async function readAll() {
  if (readingAll.value || !unread.value) return;
  readingAll.value = true;
  try {
    await pointApi.markAllNotificationsRead();
    items.value = items.value.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() }));
    unread.value = 0;
    await points.refreshNotifications();
  } catch (error) {
    ElMessage.error(pointErrorMessage(error, '全部已读操作失败，请稍后重试。'));
  } finally {
    readingAll.value = false;
  }
}

onMounted(() => {
  if (hasConsoleSession()) void refresh();
});
</script>

<style scoped>
.notification-page{max-width:980px;margin:0 auto}.notification-page header{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:20px}.notification-page header p{margin:0;color:#238078;font-size:11px;font-weight:800;letter-spacing:.18em}.notification-page h1{margin:5px 0 0;color:#182d43}.notification-page section{overflow:hidden;min-height:180px;border:1px solid #e2e9ee;border-radius:16px;background:#fff}.notification-page article{display:grid;grid-template-columns:10px 1fr;gap:14px;padding:20px 22px;border-bottom:1px solid #edf1f4;cursor:pointer}.notification-page article:last-child{border-bottom:0}.notification-page article i{width:8px;height:8px;margin-top:6px;border-radius:50%;background:#d8e0e5}.notification-page article.unread{background:#f0faf7}.notification-page article.unread i{background:#16866f;box-shadow:0 0 0 4px #d8f1eb}.title{display:flex;justify-content:space-between;gap:16px;color:#22384d}.title time{color:#8b98a3;font-size:12px;font-weight:400}.notification-page article p{margin:8px 0 0;color:#667786;line-height:1.6}@media(max-width:600px){.notification-page article{padding:16px}.title{flex-direction:column;gap:4px}}
</style>
